import {
  ChevronDown,
  CircleUserRound,
  CreditCard,
  History,
  LogOut,
  Settings,
  ShieldCheck,
  SlidersHorizontal
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { formatPlanName } from '../data/plans';

function initials(value: string) {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'L';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
}

export function AccountMenu() {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, profile, profileLoading, profileError, refreshProfile, signOut } = useAuth();

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  if (!user) return null;

  const displayName = profile?.displayName || user.displayName || user.email?.split('@')[0] || 'Nexhance AI member';
  const photoURL = profile?.photoURL || user.photoURL || '';
  const planName = profile ? formatPlanName(profile.plan) : profileLoading ? 'Loading…' : 'Unavailable';
  const creditLabel = profile
    ? `${profile.credits.toLocaleString()} credits`
    : profileLoading
      ? 'Loading credits…'
      : 'Credits unavailable';

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
      setOpen(false);
      navigate('/');
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="account-menu" ref={rootRef}>
      <button
        type="button"
        className="account-trigger"
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (nextOpen && profileError && !profileLoading) void refreshProfile();
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open account menu"
      >
        <span className="account-trigger-credit"><span className="credits-dot" />{creditLabel}</span>
        <span className="account-avatar account-avatar-small">
          {photoURL ? <img src={photoURL} alt="" referrerPolicy="no-referrer" /> : initials(displayName)}
        </span>
        <ChevronDown size={15} className={open ? 'rotated' : ''} />
      </button>

      {open && (
        <div className="account-popover" role="menu">
          <div className="account-popover-profile">
            <span className="account-avatar">
              {photoURL ? <img src={photoURL} alt="" referrerPolicy="no-referrer" /> : initials(displayName)}
            </span>
            <div>
              <strong>{displayName}</strong>
              <span>{user.email}</span>
            </div>
          </div>

          <div className="account-popover-plan">
            <div><small>Current plan</small><strong>{planName}</strong></div>
            <div><small>Available</small><strong>{creditLabel}</strong></div>
          </div>

          <nav aria-label="Account menu">
            <Link to="/settings" role="menuitem" onClick={() => setOpen(false)}><CircleUserRound size={18} /><span><strong>Account overview</strong><small>Profile, credits and activity</small></span></Link>
            <Link to="/settings?section=billing" role="menuitem" onClick={() => setOpen(false)}><CreditCard size={18} /><span><strong>Billing and plan</strong><small>Subscription and invoices</small></span></Link>
            <Link to="/settings?section=history" role="menuitem" onClick={() => setOpen(false)}><History size={18} /><span><strong>Processing history</strong><small>Recent image jobs</small></span></Link>
            <Link to="/settings?section=preferences" role="menuitem" onClick={() => setOpen(false)}><SlidersHorizontal size={18} /><span><strong>Preferences</strong><small>Appearance and defaults</small></span></Link>
            <Link to="/settings?section=security" role="menuitem" onClick={() => setOpen(false)}><ShieldCheck size={18} /><span><strong>Security</strong><small>Sign-in and session controls</small></span></Link>
          </nav>

          <div className="account-popover-footer">
            <Link to="/settings" onClick={() => setOpen(false)}><Settings size={16} /> All settings</Link>
            <button type="button" onClick={() => void handleSignOut()} disabled={signingOut}><LogOut size={16} /> {signingOut ? 'Signing out…' : 'Sign out'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
